import { FC, lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { MenuTestPage } from '../pages/MenuTestPage'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { WithChildren } from '../../_metronic/helpers'
import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'
import AccountsRoutes from '../../mogul/routes/AccountsRoutes'
import PartnersRoutes from '../../mogul/routes/PartnersRoutes'
import ServiceLocationsRoutes from '../../mogul/routes/ServiceLocationsRoutes'
import FRMOauthRoutes from '../../mogul/routes/FRMOauthRoutes'
import { DebuggingWrapper } from '../pages/debugging/DebuggingWrapper'
import PassareRoutes from '../../mogul/routes/PassareRoutes'
import FCAManageOrgDetail from '../../mogul/routes/fca/manage/FCAManageOrgDetail'
import BillingDashboardPage from '../../mogul/routes/BillingDashboardPage'
import WebLeadsPage from '../../mogul/routes/WebLeadsPage'


const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
  const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'))
  const JobsPage = lazy(() => import('../modules/jobs/JobsPageSimple'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registration */}
        <Route path='auth/frm/oauth/callback' element={<FRMOauthRoutes />} />
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        <Route path='debugging/*' element={<DebuggingWrapper />} />
        <Route path='accounts/:account_unique_identifier/*' element={<AccountsRoutes />} />
        <Route path='partners/*' element={<PartnersRoutes />} />
        <Route path='service-locations/*' element={<ServiceLocationsRoutes />} />
        <Route path='passare/*' element={<PassareRoutes />} />
        <Route path='fca/manage-org/:organization_unique_identifier/*' element={<FCAManageOrgDetail />} />
        <Route path='builder' element={<BuilderPageWrapper />} />
        <Route path='menu-test' element={<MenuTestPage />} />
        {/* Jobs Monitor - Super Users Only */}
        <Route
          path='utilities/jobs'
          element={
            <SuspensedView>
              <JobsPage />
            </SuspensedView>
          }
        />
        {/* Billing Dashboard - Restricted to specific emails */}
        <Route path='utilities/billing' element={<BillingDashboardPage />} />
        {/* Web Leads - Restricted to specific emails */}
        <Route path='utilities/web-leads' element={<WebLeadsPage />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
